import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";
import NavBar from "./components/NavBar";
import MobileHeader from "./components/MobileHeader";
import BottomNavigation from "./components/BottomNavigation";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import CompaniesPage from "./pages/CompaniesPage";
import CompanyDetailPage from "./pages/CompanyDetailPage";
import CompareCompaniesPage from "./pages/CompareCompaniesPage";
import SchedulesPage from "./pages/SchedulesPage";
import ESTemplatesPage from "./pages/ESTemplatesPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import NotFoundPage from "./pages/NotFoundPage";
import ServerErrorPage from "./pages/ServerErrorPage";
import AuthCallbackPage from "./pages/AuthCallbackPage";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminContacts from "./pages/admin/AdminContacts";
import AdminCompanyTemplates from "./pages/admin/AdminCompanyTemplates";
import AdminAuditLogs from "./pages/admin/AdminAuditLogs";

function AppLayout() {
  // useLocation must be used within Router; this small layout component
  // allows conditional rendering of the NavBar (hide on public pages).
  const location = useLocation();
  const publicPages = [
    "/",
    "/login",
    "/about",
    "/contact",
    "/terms",
    "/privacy",
    "/auth/callback",
    "/404",
    "/500",
  ];
  const hideNav = publicPages.includes(location.pathname);

  // 管理者ページではボトムナビゲーションを表示しない
  const isAdminPage = location.pathname.startsWith("/admin");
  const showBottomNav = !hideNav && !isAdminPage;

  return (
    <div className="App">
      {/* PC用ナビゲーションバー（768px以上） */}
      {!hideNav && <NavBar />}

      {/* モバイル用ヘッダー（768px未満） */}
      {!hideNav && <MobileHeader />}

      <Routes>
        {/* 公開ルート */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/404" element={<NotFoundPage />} />
        <Route path="/500" element={<ServerErrorPage />} />

        {/* 保護されたルート */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/companies/:id"
          element={
            <ProtectedRoute>
              <CompanyDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/companies"
          element={
            <ProtectedRoute>
              <CompaniesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/compare-companies"
          element={
            <ProtectedRoute>
              <CompareCompaniesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/schedules"
          element={
            <ProtectedRoute>
              <SchedulesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/es-templates"
          element={
            <ProtectedRoute>
              <ESTemplatesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/applications"
          element={<Navigate to="/companies" replace />}
        />

        {/* 管理者ルート */}
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <AdminRoute>
              <AdminUsers />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/contacts"
          element={
            <AdminRoute>
              <AdminContacts />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/company-templates"
          element={
            <AdminRoute>
              <AdminCompanyTemplates />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/audit-logs"
          element={
            <AdminRoute>
              <AdminAuditLogs />
            </AdminRoute>
          }
        />

        {/* ルートパスはAboutページ（ホームページ）を直接表示 */}
        <Route path="/" element={<AboutPage />} />
        {/* 404ページ - 存在しないパスへのアクセス */}
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Routes>

      {/* モバイル用ボトムナビゲーション */}
      {showBottomNav && <BottomNavigation />}
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <Router>
          <AppLayout />
        </Router>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
